// Client/src/components/PromoCodeInput.tsx
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API_BASE = import.meta.env.VITE_BASE_API_URL;

export interface AppliedPromoDetails {
  code: string;
  source: "voucher" | "influencer";
  discountType: string | null;
  discountValue: number | null;
  bonusCredits: number | null;
  bonusCreditsGranted: boolean;
  autoApplied: boolean;
}

interface PromoCodeInputProps {
  // Omit for a non-subscription purchase (e.g. architect hours) — the free-plan check and
  // influencer-code fallback only apply when there's a plan to check against.
  planName?: string;
  originalPrice: number;  // plan's base price in dollars, used to display final price after discount
  onApplied: (details: AppliedPromoDetails) => void;
  onCleared: () => void;
  authToken: string;
  // The workspace this checkout is for. A fresh influencer code grants bonus credits
  // immediately, and they must be attributed to this same workspace or they're excluded
  // from its credit balance — omit only when no workspace exists yet (e.g. new team creation).
  workspaceId?: number | null;
  // Gate off the mount-time GET /promo-codes/pending pre-fill (unused influencer discount, or a signup voucher re-validated for this purchase)
  checkPending?: boolean;
  // Which non-subscription purchase this is (ignored when planName is set) — the voucher must be scoped to it.
  purchaseType?: "architect_hours" | "credits";
}

interface ValidationResult {
  valid: boolean;
  error?: string;
  source?: "voucher" | "influencer";
  code?: string;
  discount_type?: string | null;
  discount_value?: number | null;
  bonus_credits?: number | null;
  bonus_credits_granted?: boolean;
  newly_applied?: boolean;
}

export function PromoCodeInput({ planName, originalPrice, onApplied, onCleared, authToken, workspaceId, checkPending = true, purchaseType = "architect_hours" }: PromoCodeInputProps) {
  const [code, setCode] = useState("");
  const [appliedCode, setAppliedCode] = useState<string | null>(null);
  const [appliedDiscountType, setAppliedDiscountType] = useState<string | null>(null);
  const [appliedDiscountValue, setAppliedDiscountValue] = useState<number | null>(null);
  const [appliedBonusCredits, setAppliedBonusCredits] = useState<number | null>(null);
  const [appliedNewlyApplied, setAppliedNewlyApplied] = useState(false);
  const [autoApplied, setAutoApplied] = useState(false);
  const [appliedSource, setAppliedSource] = useState<"voucher" | "influencer">("voucher");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [checkingPending, setCheckingPending] = useState(true);

  const applyResult = (data: ValidationResult, wasAutoApplied: boolean) => {
    const upper = (data.code || "").toUpperCase();
    setAppliedCode(upper);
    setAppliedDiscountType(data.discount_type ?? null);
    setAppliedDiscountValue(data.discount_value ?? null);
    setAppliedBonusCredits(data.bonus_credits ?? null);
    setAppliedNewlyApplied(!!data.newly_applied);
    setAutoApplied(wasAutoApplied);
    setAppliedSource(data.source ?? "voucher");
    onApplied({
      code: upper,
      source: data.source ?? "voucher",
      discountType: data.discount_type ?? null,
      discountValue: data.discount_value ?? null,
      bonusCredits: data.bonus_credits ?? null,
      bonusCreditsGranted: !!data.bonus_credits_granted,
      autoApplied: wasAutoApplied,
    });
  };

  useEffect(() => {
    if (!authToken || !checkPending) {
      setCheckingPending(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // Same purchase context as /validate, so a pending signup voucher is re-checked against what's being bought
        const params = new URLSearchParams();
        if (planName) params.set("plan_name", planName);
        else params.set("purchase_type", purchaseType);
        const res = await fetch(`${API_BASE}/promo-codes/pending?${params.toString()}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const data: ValidationResult = await res.json();
        if (!cancelled && data.valid) {
          applyResult(data, true);
        }
      } catch {
        // Pre-filling a pending code is a nice-to-have — a failed check just leaves the box empty.
      } finally {
        if (!cancelled) setCheckingPending(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  const handleApply = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/promo-codes/validate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          code: code.trim(),
          plan_name: planName,
          workspace_id: workspaceId ?? undefined,
          purchase_type: planName ? undefined : purchaseType,
        }),
      });
      const data: ValidationResult = await res.json();

      if (data.valid) {
        applyResult({ ...data, code: data.code || code.trim() }, false);
      } else {
        setError(data.error || "Code is invalid or inactive");
      }
    } catch {
      setError("Failed to validate code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setCode("");
    setAppliedCode(null);
    setAppliedDiscountType(null);
    setAppliedDiscountValue(null);
    setAppliedBonusCredits(null);
    setAppliedNewlyApplied(false);
    setAutoApplied(false);
    setError(null);
    onCleared();
  };

  const computeSummary = (): string => {
    const parts: string[] = [];
    if (appliedBonusCredits) {
      parts.push(
        `${appliedBonusCredits.toLocaleString()} bonus credits${appliedNewlyApplied ? " added to your account" : " (added at signup)"}`
      );
    }
    if (appliedDiscountType && appliedDiscountValue !== null) {
      const finalPrice = appliedDiscountType === "percentage"
        ? originalPrice * (1 - appliedDiscountValue / 100)
        : Math.max(originalPrice - appliedDiscountValue, 0);
      const discountLabel = appliedDiscountType === "percentage"
        ? `${appliedDiscountValue}% off`
        : `$${appliedDiscountValue} off`;
      const priceSuffix = !planName && purchaseType === "credits" ? "" : "/mo";
      parts.push(`${discountLabel} → $${finalPrice.toFixed(2)}${priceSuffix}`);
    }
    return parts.join(" + ");
  };

  if (checkingPending) {
    return <div className="h-9" />;
  }

  if (appliedCode) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <span className="text-green-600 dark:text-green-400">✓ {appliedCode} applied — {computeSummary()}</span>
        {autoApplied && (
          <span className="text-muted-foreground text-xs">
            {appliedSource === "influencer" ? "(from your referral code)" : "(from your signup code)"}
          </span>
        )}
        <button onClick={handleClear} className="text-muted-foreground hover:text-foreground underline text-xs">
          Remove
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex gap-2">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="Promo code"
          className="bg-white dark:bg-white/5 border-line text-foreground placeholder:text-muted-foreground uppercase focus:border-plum/40 focus:ring-ring/40"
          onKeyDown={(e) => e.key === "Enter" && handleApply()}
          disabled={loading}
        />
        <Button
          variant="outline"
          onClick={handleApply}
          disabled={loading || !code.trim()}
          className="border-line text-muted-foreground hover:text-foreground hover:bg-soft"
        >
          {loading ? "..." : "Apply"}
        </Button>
      </div>
      {error && <p className="text-red-600 dark:text-red-400 text-xs">{error}</p>}
    </div>
  );
}
